import { Trash01 } from "@untitled-ui/icons-react";
import { useTranslation } from "react-i18next";

import Input from "@/components/forms/Input";
import Legend from "@/components/helpers/Legend";
import type { Media } from "@/types";

import type { GroupDetails } from "../../services/group";
import GroupImageUpload from "./GroupImageUpload";
import GroupSettingsRow from "./GroupSettingsRow";
import GroupSubPageHeader from "./GroupSubPageHeader";

type GroupEditMainViewProps = {
  name: string;
  groupDetails?: GroupDetails;
  imageMedia: Media | null;
  imageRemoved: boolean;
  managerCount: number;
  memberCount: number;
  isUploading?: boolean;
  isSaving?: boolean;
  onNameChange: (name: string) => void;
  onImageUpload: (media: Media) => void;
  onImageDelete: () => void;
  onBack: () => void;
  onConfirm: () => void;
  onTypeClick: () => void;
  onManagersClick: () => void;
  onMembersClick: () => void;
  onDeleteLeave: () => void;
};

export default function GroupEditMainView({
  name,
  groupDetails,
  imageMedia,
  imageRemoved,
  managerCount,
  memberCount,
  isUploading,
  isSaving,
  onNameChange,
  onImageUpload,
  onImageDelete,
  onBack,
  onConfirm,
  onTypeClick,
  onManagersClick,
  onMembersClick,
  onDeleteLeave,
}: GroupEditMainViewProps) {
  const { t } = useTranslation("messenger.user");

  const typeLabel =
    groupDetails?.type === "public" ? t("Public Group") : t("Private Group");

  const imageUrl = imageRemoved ? null : groupDetails?.image_thumb;

  return (
    <div className="flex h-full flex-col bg-white">
      <GroupSubPageHeader
        title={t("Edit")}
        onBack={onBack}
        onConfirm={onConfirm}
        isConfirmLoading={isSaving}
      />

      <div className="flex items-center gap-3 p-4">
        <div className="shrink-0">
          <GroupImageUpload
            imageUrl={imageUrl}
            imageMedia={imageMedia}
            isUploading={isUploading}
            onUpload={onImageUpload}
            onDelete={onImageDelete}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Input value={name} onChange={(e) => onNameChange(e.target.value)} />
        </div>
      </div>

      <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-600">
        {t("Group settings")}
      </div>

      <GroupSettingsRow
        label={t("Group type")}
        value={typeLabel}
        onClick={onTypeClick}
      />
      <Legend className="mx-6" />
      <GroupSettingsRow
        label={t("Managers")}
        value={`${managerCount}`}
        onClick={onManagersClick}
      />
      <Legend className="mx-6" />
      <GroupSettingsRow
        label={t("Members")}
        value={`${memberCount}`}
        onClick={onMembersClick}
      />

      {(groupDetails?.can_delete || groupDetails?.can_leave) && (
        <>
          <Legend className="mx-6" />
          <button
            type="button"
            className="text-error-600 flex w-full items-center gap-2 px-6 py-4 text-sm font-medium hover:bg-gray-50"
            onClick={onDeleteLeave}
          >
            <Trash01 className="size-5" />
            {groupDetails.can_delete
              ? t("Delete and leave group")
              : t("Leave group")}
          </button>
        </>
      )}
    </div>
  );
}
